// Evenly spaced ticks for a time axis, the way the QuickLook report's Plotly
// axes place them. ECharts' own time ticks always add one at every month start,
// so "25, 29, Oct, 3, 5, 9" ends up unevenly spaced around each new month.

const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const AVG_MONTH_MS = 30.4375 * DAY_MS;
// Plotly's step sets and density rule (axes.js autoTicks): it aims for
// clamp(width / 80, 4, 9) + 1 ticks, rounds the rough step up to the next
// "nice" value, and only switches to months once that rough step passes half
// a month. Below that it keeps 14-day ticks even if a few too many. Zoomed in
// past half a day per tick it steps in hours, or a window between two
// midnights would get no ticks at all.
const HOUR_STEPS = [1, 2, 3, 6, 12];
const DAY_STEPS = [1, 2, 3, 7, 14];
const MONTH_STEPS = [1, 2, 3, 6, 12];
const PX_PER_TICK = 80;
// Day steps count from here so ticks don't shift as the window pans. A Sunday,
// so 7- and 14-day steps land on Sundays, as Plotly's do.
const DAY_EPOCH = new Date(2000, 0, 2).getTime();

export type EvenTimeTicks = {
  values: number[];
  format: (ms: number) => string;
};

export function evenTimeTicks(
  minMs: number,
  maxMs: number,
  widthPx: number,
): EvenTimeTicks {
  const tickCount = Math.min(Math.max(widthPx / PX_PER_TICK, 4), 9) + 1;
  const rough = (maxMs - minMs) / tickCount;
  if (rough * 2 <= DAY_MS) {
    const step = roundUp(rough / HOUR_MS, HOUR_STEPS);
    return { values: hourTicks(minMs, maxMs, step), format: hourLabel };
  }
  if (rough * 2 <= AVG_MONTH_MS) {
    const step = roundUp(rough / DAY_MS, DAY_STEPS);
    return { values: dayTicks(minMs, maxMs, step), format: dayLabel };
  }
  const months = rough / AVG_MONTH_MS;
  // Past a year Plotly steps in whole years; a 12-month multiple does the same.
  const step =
    months > 12 ? Math.ceil(months / 12) * 12 : roundUp(months, MONTH_STEPS);
  return { values: monthTicks(minMs, maxMs, step), format: monthLabel };
}

// Smallest step that is at least `value`; the largest when none is.
function roundUp(value: number, steps: number[]): number {
  return steps.find((s) => s >= value) ?? steps[steps.length - 1];
}

// Local hours a multiple of `step` from midnight. Stepped with setHours, like
// dayTicks, so the hours stay put around a DST change.
function hourTicks(minMs: number, maxMs: number, step: number): number[] {
  const d = new Date(minMs);
  d.setMinutes(0, 0, 0);
  d.setHours(Math.ceil(d.getHours() / step) * step);
  const ticks: number[] = [];
  for (; d.getTime() <= maxMs; d.setHours(d.getHours() + step)) {
    if (d.getTime() >= minMs) ticks.push(d.getTime());
  }
  return ticks;
}

// Local midnights a multiple of `step` days from DAY_EPOCH. Stepped with
// setDate, not by adding ms, so a DST change can't knock them off midnight.
function dayTicks(minMs: number, maxMs: number, step: number): number[] {
  const d = new Date(minMs);
  d.setHours(0, 0, 0, 0);
  const index = Math.round((d.getTime() - DAY_EPOCH) / DAY_MS);
  d.setDate(d.getDate() + (((-index % step) + step) % step));
  const ticks: number[] = [];
  for (; d.getTime() <= maxMs; d.setDate(d.getDate() + step)) {
    if (d.getTime() >= minMs) ticks.push(d.getTime());
  }
  return ticks;
}

// Month starts whose month index is a multiple of `step` (so quarters start in
// Jan/Apr/Jul/Oct).
function monthTicks(minMs: number, maxMs: number, step: number): number[] {
  const start = new Date(minMs);
  let index = start.getFullYear() * 12 + start.getMonth();
  index += (step - (index % step)) % step;
  const ticks: number[] = [];
  for (; ; index += step) {
    const t = new Date(Math.floor(index / 12), index % 12, 1).getTime();
    if (t > maxMs) break;
    if (t >= minMs) ticks.push(t);
  }
  return ticks;
}

// "en-US" pinned, as heartcore-format does, so labels don't follow the viewer.
// With the date: a window under a day can still span midnight.
function hourLabel(ms: number): string {
  return new Date(ms).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  });
}

function dayLabel(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function monthLabel(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
}
